import React from 'react';

import { useGetAssessment } from '@hooks/api/assessment';

import type { AssessmentResultData } from '@models/assessment/result';
import { fromGetAssessmentQuery } from '@models/assessment/result';

type AssessmentResultState = {
  /** Category of assessment: BHA, AHA, etc. */
  category?: string | null;
  /** ID of assessment */
  id?: string | null;
  isInitialized: boolean;
};

export enum AssessmentResultActions {
  Initialize = 'initialize',
}

type Payload = {
  [AssessmentResultActions.Initialize]: {
    id: string;
  };
};

const initialAssessmentState: AssessmentResultState = {
  id: null,
  category: null,
  isInitialized: false,
};

function reducer(
  state: AssessmentResultState,
  action: Action<Payload>
): AssessmentResultState {
  switch (action.type) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    case AssessmentResultActions.Initialize:
      return {
        ...state,
        isInitialized: true,
      };
    default:
      return state;
  }
}

export const AssessmentResultContext = React.createContext<{
  state: AssessmentResultState;
  dispatch: React.Dispatch<Action<Payload>>;
  loading: boolean;
  data: AssessmentResultData | undefined;
}>({
  state: initialAssessmentState,
  dispatch: () => null,
  loading: false,
  data: undefined,
});

interface AssessmentResultProviderProps {
  children: React.ReactNode;
  /** Assessment to fetch. Authenticated views only. */
  id?: string;
  /**
   * Pre-resolved result, for contexts that cannot run the authenticated query --
   * currently the external share page, which receives the assessment through
   * `getSharedResource` instead. When provided, no query is issued.
   */
  result?: AssessmentResultData;
  /** Loading state of whatever produced `result`. Ignored in `id` mode. */
  loading?: boolean;
}

export const AssessmentResultProvider = ({
  children = null,
  id,
  result,
  loading: providedLoading = false,
}: AssessmentResultProviderProps) => {
  const isProvided = result !== undefined;
  const [state, dispatch] = React.useReducer(reducer, {
    ...initialAssessmentState,
    id: id ?? null,
  });

  // Still called in provided mode so hook order stays stable across both, but
  // skipped so the external page issues no authenticated request.
  const { data, loading: queryLoading } = useGetAssessment(
    { assessment_id: state.id ?? '' },
    isProvided
  );

  // Left undefined until there is something real to show, so consumers can keep
  // treating a defined `data` as "the result has arrived".
  const resolved = React.useMemo(
    () =>
      isProvided ? result : data ? fromGetAssessmentQuery(data) : undefined,
    [isProvided, result, data]
  );
  const loading = isProvided ? providedLoading : queryLoading;

  const value = React.useMemo(
    () => ({ state, dispatch, loading, data: resolved }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, loading, resolved]
  );

  return (
    <AssessmentResultContext.Provider value={value}>
      {children}
    </AssessmentResultContext.Provider>
  );
};
